import React from "react";
import PropTypes from "prop-types";

import WdSelect from "./wd_select";

const stripDiacritics = s => String(s).normalize("NFD").replace(/[̀-ͯ]/g, "");

const LABEL_KEY = "label";

// Assume we are using
// ignoreAccents = true,
// ignoreCase = true,
// matching on labels only,
// no excluded options,
// no filterOption override.
// matchPos = any
const filterOptions = function(options, filterValue) {
  filterValue = stripDiacritics(filterValue).toLowerCase();

  const firstPass = options.filter(option => {
    if (!filterValue) return true;

    let labelTest = stripDiacritics(String(option[LABEL_KEY])).toLowerCase();

    return labelTest.indexOf(filterValue) >= 0;
  });

  // Perform our overriden search.
  // The goals are:
  // 1. Any option that passed the firstPass should also pass here
  // 2. Any child of an option that passed the firstPass should also pass here
  // 3. Any parent or grandparent of an option that passed the firstPass should also pass here
  // TODO(SI): just how awful is the O-complexity here? The previous one is linear. Are we at least staying O(n^2)?
  return options.filter(option => {
    if (firstPass.indexOf(option) > -1) { return true; }

    if (option.type === "group") {
      return firstPass.filter(o => o.group === option.label).length > 0;
    } else if (option.type === "subGroup") {
      return firstPass.filter(o => (o.subGroup === option.label || o.label === option.group)).length > 0;
    } else {
      const optFilter = o => (o.label === option.subGroup || o.label === option.group);
      return firstPass.filter(optFilter).length > 0;
    }
  });
};


class GroupedMultiSelect extends React.Component {
  constructor(props) {
    super(props);

    this.state = { inputValue: props.initialTextValue || "" };
  }

  componentDidUpdate(prevProps) {
    // Re-seed the controlled input when the externally-supplied initial text changes.
    if (prevProps.initialTextValue !== this.props.initialTextValue) {
      this.setState({ inputValue: this.props.initialTextValue || "" });
    }
  }

  render () {
    const selectedValues = this.props.selectedValues;

    // v5 Option component. v1's optionRenderer render-prop receives
    // {focusedOption, focusOption, key, ..., option, selectValue, ...}; v5's
    // Option receives {data, isFocused, innerProps, children}. The app-emitted
    // className logic (groupedOption / groupedHeader / group / subGroup / option /
    // groupedOptionFocused / groupedOptionSelected) is ported verbatim so the
    // local SCSS keeps styling the rendered DOM. v5 handles selection, so we drop
    // the manual selectValue(option) and use innerProps for hover/click on real
    // options; group/subGroup headers render as non-interactive divs.
    // eslint-disable-next-line react/prop-types
    const CustomOption = ({ data, isFocused, innerProps }) => {
      const option = data;
      const classNames = ["groupedOption"];

      if (option.type === "subGroup" || option.type === "group") {
        classNames.push("groupedHeader");
        classNames.push(option.type);

        return (
          <div className={classNames.join(" ")}>
            {option.label}
          </div>
        );
      }

      if (isFocused) {
        classNames.push("groupedOptionFocused");
      }

      classNames.push("option");

      if (selectedValues.find((e) => e.value === option.value)) {
        classNames.push("groupedOptionSelected");
        return (
          <div className={classNames.join(" ")}>
            {option.label}
          </div>
        );
      }

      return (
        <div
          {...innerProps}
          className={classNames.join(" ")}
        >
          {option.label}
        </div>
      );
    };

    const formName = this.props.formName || "not-used";

    const onChange = (selectedOptions) => {
      const options = selectedOptions || [];
      const uniqueSelectedOptions = [];
      const seenValues = [];

      options.map(e => {
        if (!seenValues.includes(e.value)) {
          uniqueSelectedOptions.push(e);
          seenValues.push(e.value);
        }
      });

      // TODO(SI): this whole mess is meant to detect duplicate selections and prevent them. There must be a better way.
      if (options.length !== uniqueSelectedOptions.length) {
        return;
      }

      this.props.onChange(uniqueSelectedOptions);
    };

    const onInputChange = (inputValue, { action }) => {
      // Only treat user typing as a value change; v5 also fires onInputChange on
      // menu-close/blur/select with action "menu-close"/"set-value" etc. Keep the
      // typed text on close/blur (v1's onCloseResetsInput/onBlurResetsInput=false).
      if (action !== "input-change") {
        return inputValue;
      }

      this.setState({ inputValue });

      if (this.props.onInputChange) {
        this.props.onInputChange(inputValue);
      }

      return inputValue;
    };

    // Run the bespoke hierarchical filter over the full option list and pass the
    // RESULT as options, disabling v5's per-option filter (filterOption => true)
    // so the pre-filtered set is shown as-is (v5's per-option filterOption cannot
    // see "is this the parent of a match").
    const visibleOptions = filterOptions(this.props.values, this.state.inputValue);

    return (
      <WdSelect
        ref={(input) => this.input = input }
        value={selectedValues}
        name={formName}
        options={visibleOptions}
        onChange={onChange}
        isClearable={this.props.clearable}
        isMulti={true}
        inputValue={this.state.inputValue}
        onInputChange={onInputChange}
        filterOption={() => true}
        components={{ Option: CustomOption }}
        maxMenuHeight={400}
        placeholder={this.props.placeholder}
        isDisabled={this.props.disabled}
      />
    );
  }
}

const optionShape = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
});
GroupedMultiSelect.propTypes = {
  values: PropTypes.arrayOf(optionShape).isRequired,
  selectedValues: PropTypes.arrayOf(optionShape).isRequired,
  onChange: PropTypes.func.isRequired,
  clearable: PropTypes.bool.isRequired,
  formName: PropTypes.string,
  placeholder: PropTypes.string,
  onInputChange: PropTypes.func,
  initialTextValue: PropTypes.string,
  disabled: PropTypes.bool
};

export default GroupedMultiSelect;
